// Rolling p50 identify-latency store + ETA math for bulk uploads.
//
// The store lives in localStorage under `prairie.identify.durations` as a
// simple JSON array of the last N durations in milliseconds. p50 self-
// calibrates to the user's network + Gemini's current mood so the ETA
// gets more accurate the more the user uses the app.
//
// Bootstraps to a sensible 20-second per-item default when we have no
// history at all — matches typical Gemini 3 Flash single-image round-trip.

const STORAGE_KEY = "prairie.identify.durations";
const MAX_SAMPLES = 20;
const DEFAULT_ESTIMATE_MS = 20_000;

const readSamples = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((n) => typeof n === "number" && n > 0 && Number.isFinite(n))
      .slice(-MAX_SAMPLES);
  } catch {
    return [];
  }
};

const writeSamples = (arr) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(-MAX_SAMPLES)));
  } catch {
    /* private-mode / quota */
  }
};

/** Push a batch of new durations (ms). Silently no-ops for invalid input. */
export const recordIdentifyDurations = (durationsMs) => {
  if (!Array.isArray(durationsMs) || durationsMs.length === 0) return;
  const valid = durationsMs.filter((n) => typeof n === "number" && n > 500 && n < 120_000);
  if (!valid.length) return;
  writeSamples([...readSamples(), ...valid]);
};

/** Median of stored samples, or DEFAULT_ESTIMATE_MS when empty. */
export const p50IdentifyMs = () => {
  const arr = readSamples();
  if (!arr.length) return DEFAULT_ESTIMATE_MS;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

/** How many identify durations we've observed in this browser (0..MAX_SAMPLES). */
export const identifySampleCount = () => readSamples().length;

/**
 * Compute remaining wall-time for a running bulk identify batch.
 *
 * @param queueItems  Array of items with `status` (pending/processing/done/
 *                    error/queued) and (for processing items) `startedAt`.
 * @param concurrency Max parallel workers (mirror of hook constant).
 * @param baselineMs  Best-guess per-item duration from p50IdentifyMs().
 * @returns { elapsedMs, remainingMs, totalMs } where remainingMs floors at 0.
 */
export const computeBulkEta = (queueItems, concurrency, baselineMs, batchStartedAt) => {
  const now = Date.now();
  const elapsedMs = Math.max(0, now - batchStartedAt);

  // Buckets.
  const inFlight = queueItems.filter((it) => it.status === "processing");
  const pending = queueItems.filter((it) => it.status === "pending");
  const finished = queueItems.filter((it) =>
    it.status === "done" || it.status === "queued" || it.status === "error"
  );

  // Blend the caller's baseline with observed in-batch avg once we have
  // ≥ 2 finished samples — those best reflect the current server /
  // network conditions.
  const inBatchDurations = finished
    .map((it) => it.durationMs)
    .filter((n) => typeof n === "number" && n > 500);
  let effectivePer = baselineMs;
  if (inBatchDurations.length >= 2) {
    const avg = inBatchDurations.reduce((a, b) => a + b, 0) / inBatchDurations.length;
    // 60/40 blend toward the in-batch signal — it's small-sample but
    // matches the actual conditions we're currently in.
    effectivePer = 0.6 * avg + 0.4 * baselineMs;
  } else if (inBatchDurations.length === 1) {
    effectivePer = 0.35 * inBatchDurations[0] + 0.65 * baselineMs;
  }

  // Remaining work per in-flight item = whatever's left of effectivePer.
  const runningRemaining = inFlight.reduce((sum, it) => {
    const started = it.startedAt || now;
    const alreadySpent = now - started;
    return sum + Math.max(2_000, effectivePer - alreadySpent);
  }, 0);
  const pendingWork = pending.length * effectivePer;
  const totalRemainingWork = runningRemaining + pendingWork;

  // With N workers running in parallel, wall-time = totalWork / N. Round up
  // to the nearest second so we don't oscillate between two neighbouring
  // integers during the UI's 1-second re-tick.
  const workers = Math.max(1, Math.min(concurrency, inFlight.length + pending.length));
  const remainingMs = Math.max(0, Math.ceil(totalRemainingWork / workers));

  return {
    elapsedMs,
    remainingMs,
    totalMs: elapsedMs + remainingMs,
  };
};

/** Format an interval in ms as "M:SS" (or "SS s" when < 60s). */
export const formatEtaMs = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return "0s";
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};
